import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getUserConversationApi,
  getUserMessagesApi,
  sendUserMessageApi,
} from "../../api/messagingApi";

export const fetchUserConversation = createAsyncThunk(
  "userMessaging/fetchConversation",
  async (_, thunkAPI) => {
    try {
      const data = await getUserConversationApi();
      return data.conversation;
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const fetchUserMessages = createAsyncThunk(
  "userMessaging/fetchMessages",
  async (page = 1, thunkAPI) => {
    try {
      const data = await getUserMessagesApi(page);
      return { messages: data.messages, conversationId: data.conversationId };
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const fetchMoreUserMessages = createAsyncThunk(
  "userMessaging/fetchMoreMessages",
  async (page, thunkAPI) => {
    try {
      const data = await getUserMessagesApi(page);
      return { messages: data.messages };
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const sendUserMessage = createAsyncThunk(
  "userMessaging/sendMessage",
  async (payload, thunkAPI) => {
    try {
      const data = await sendUserMessageApi(payload);
      return data.message;
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

const messagingSlice = createSlice({
  name: "userMessaging",
  initialState: {
    conversation: null,
    messages: [],
    unreadCount: 0,
    isOpen: false,
    loading: false,
    loadingMore: false,
    hasMore: true,
    sending: false,
    initialized: false,
  },
  reducers: {
    setChatOpen(state, action) {
      state.isOpen = action.payload;
      if (action.payload) state.unreadCount = 0;
    },
    appendMessage(state, action) {
      const msg = action.payload;
      const exists = state.messages.find((m) => m._id === msg._id);
      if (!exists) {
        state.messages.push(msg);
        if (msg.senderType === "admin" && !state.isOpen) {
          state.unreadCount += 1;
        }
        if (state.conversation) {
          state.conversation.lastMessage =
            msg.content || (msg.attachmentType === "image" ? "📷 Image" : "");
          state.conversation.lastMessageAt = msg.createdAt;
        }
      }
    },
    clearUnread(state) {
      state.unreadCount = 0;
    },
    updateMessageStatus(state, action) {
      const { messageIds, status, senderType } = action.payload;
      const idSet = new Set(messageIds);
      state.messages = state.messages.map((m) => {
        if (!idSet.has(m._id)) return m;
        const sType = senderType || m.senderType || 'user';
        if (sType === 'admin') {
          if (status === 'delivered') return { ...m, deliveredToUser: true };
          if (status === 'read') return { ...m, deliveredToUser: true, readByUser: true };
        } else {
          if (status === 'delivered') return { ...m, deliveredToAdmin: true };
          if (status === 'read') return { ...m, deliveredToAdmin: true, readByAdmin: true };
        }
        return m;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserConversation.fulfilled, (state, action) => {
        state.conversation = action.payload;
        state.initialized = true;
        // Seed unread count from server so the badge shows on page load
        if (action.payload?.userUnreadCount) {
          state.unreadCount = action.payload.userUnreadCount;
        }
      })
      .addCase(fetchUserConversation.rejected, (state) => {
        state.initialized = true;
      })
      .addCase(fetchUserMessages.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload.messages;
        // If fewer than 50 returned, no older pages exist
        state.hasMore = action.payload.messages.length >= 50;
      })
      .addCase(fetchUserMessages.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchMoreUserMessages.pending, (state) => {
        state.loadingMore = true;
      })
      .addCase(fetchMoreUserMessages.fulfilled, (state, action) => {
        state.loadingMore = false;
        const older = action.payload.messages;
        // Deduplicate then prepend older messages
        const existingIds = new Set(state.messages.map((m) => m._id));
        const newOnes = older.filter((m) => !existingIds.has(m._id));
        state.messages = [...newOnes, ...state.messages];
        state.hasMore = older.length >= 50;
      })
      .addCase(fetchMoreUserMessages.rejected, (state) => {
        state.loadingMore = false;
      })
      .addCase(sendUserMessage.pending, (state) => {
        state.sending = true;
      })
      .addCase(sendUserMessage.fulfilled, (state, action) => {
        state.sending = false;
        const msg = action.payload;
        const exists = state.messages.find((m) => m._id === msg._id);
        if (!exists) state.messages.push(msg);
      })
      .addCase(sendUserMessage.rejected, (state) => {
        state.sending = false;
      });
  },
});

export const { setChatOpen, appendMessage, clearUnread, updateMessageStatus } = messagingSlice.actions;
export default messagingSlice.reducer;
